import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import ProtectedRoute from '../../ProtectedRoute';

// Import page components
import DashboardContent from '../dashboard/index';
import Salon from '../salon/salon';
import SalonDetail from '../salon/salonDetail';
import SalonThemeSettingsPage from '../salon/SalonThemeSettingsPage';
import Service from '../salon/service/service';
import Category from '../salon/category/category';
import SalonClosures from '../salon/salonClosures';
import Appointment from '../appointment/appointment';
import Team from '../employee/team/team';
import TeamMembers from '../employee/teamMember/team_members';
import Schedule from '../employee/schedule/schedule';
import EmployeeDashboard from '../employee/EmployeeDashboard';
import ClientData from '../clientData/clientData';
import Profile from '../profile/Profile';
import SubscriptionEntry from '../subscriptions/SubscriptionEntry';
import SalonManagerMessaging from '../messaging/SalonManagerMessaging';
import ManagerAccessPage from '../access/ManagerAccessPage';
import SalonPermissionGate, { firstPermittedProfessionalRoute } from '../access/SalonPermissionGate';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import ClientInboxPage from '../messaging/ClientInboxPage';
import SalonClientMessagesGate from '../access/SalonClientMessagesGate';

/**
 * Protected Route component - wraps routes that require authentication
 */
 

/**
 * Smart redirect component that redirects based on user role
 */
const SmartRedirect = () => {
  const { user } = useAuth();
  const { canSee, isLoading } = useSalonAccess();
  
  console.log('SmartRedirect - user role:', user?.role);
  
  // Redirect employees to their dashboard, managers to main dashboard
  // Using relative paths to stay within the /pro/* route context
  if (user?.role === 'professional_employe') {
    console.log('Redirecting to employee dashboard');
    return <Navigate to="employe/dashboard" replace />;
  }

  if (['professional_manager', 'professional_manager_employee'].includes(user?.role)) {
    if (isLoading) return null;
    return <Navigate to={firstPermittedProfessionalRoute(canSee)} replace />;
  }
  
  console.log('Redirecting to manager dashboard');
  return <Navigate to="dashboard" replace />;
};

/**
 * Main application routes
 */
const AppRoutes = () => {
  return (
    <Routes>
      {/* Dashboard - default route with smart redirect */}
      <Route index element={<SmartRedirect />} />
      <Route path="dashboard" element={<DashboardContent />} />
      
      {/* Salon routes */}
      <Route path="salon" element={<Salon />} />
      <Route path="salon/fermetures" element={<SalonClosures />} />
      <Route path="salon/:salonId" element={<SalonDetail />} />
      <Route path="salon/:salonId/theme" element={<SalonThemeSettingsPage />} />
      
      {/* Service routes */}
      <Route path="salon/service" element={<Service />} />
      
      {/* Category routes */}
      <Route path="salon/category" element={<Category />} />
      
      {/* Employee routes - Using a more explicit nested route structure */}
      <Route path="employe" element={<Outlet />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<EmployeeDashboard />} />
        <Route path="groupe" element={<Team />} />
        <Route path="membre-de-groupe" element={<TeamMembers />} />
        <Route path="planning" element={<Schedule />} />
      </Route>
      
      {/* Alternative path with accent for better UX */}
      <Route path="employé" element={<Navigate to="employe" replace />} />
      
      {/* Appointment routes */}
      <Route path="rendez-vous" element={<Appointment />} />

      <Route
        path="gestionnaires"
        element={
          <SalonPermissionGate permission="managers.view">
            <ManagerAccessPage />
          </SalonPermissionGate>
        }
      />

      {/* Client routes */}
      <Route
        path="client"
        element={
          <ProtectedRoute requiredAction="read" requiredSubject="users" fallbackRoute="/pro/dashboard">
            <ClientData />
          </ProtectedRoute>
        }
      />
      
      {/* Subscription selection (public for authenticated pro without active subscription) */}
      <Route path="subscribe" element={<SubscriptionEntry />} />
      
      {/* Profile route */}
      <Route path="profile" element={<Profile />} />

      <Route
        path="messaging"
        element={
          <ProtectedRoute requiredAction="read" requiredSubject="messaging" fallbackRoute="/pro/dashboard">
            <SalonManagerMessaging />
          </ProtectedRoute>
        }
      />
      <Route
        path="client-messages"
        element={
          <SalonClientMessagesGate>
            <ClientInboxPage />
          </SalonClientMessagesGate>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
